import { getAllVariants, upsertVariant } from '@/lib/optimizeVariants';

// Storage adapter for the Schedule workspace. Reads/writes the SAME
// `consistency_scheduled_posts` array the legacy `pages/Scheduler.jsx`
// already writes (`id`, `variantId`, `platform`, `scheduledAt`, `title`,
// `description`, `hashtags`, `createdAt`) — nothing here renames or
// removes those fields, so legacy records and the legacy page keep
// working untouched. New fields below are purely additive.
const POSTS_KEY = 'consistency_scheduled_posts';
const TIMEZONE_KEY = (creatorId) => `consistency_creator_timezone_${creatorId}`;

const newId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

export function getAllScheduledPosts() {
  return readJson(POSTS_KEY, []);
}
export function saveAllScheduledPosts(list) {
  localStorage.setItem(POSTS_KEY, JSON.stringify(list));
}

/**
 * Content Queue: every approved-and-not-yet-scheduled Optimize variant
 * (`status === 'ready'`, exactly the field `Scheduler.jsx` already
 * filters on), grouped by the content asset they belong to so a single
 * video/clip/design shows ONE queue card with all of its ready platforms
 * — matching the "a single asset, multiple platform-specific posts"
 * rule from section 7, which Optimize already encodes as one variant
 * record per platform.
 */
export function getQueueGroups({ projectId, creatorId, scope } = {}) {
  const ready = getAllVariants().filter((v) => v.status === 'ready');
  const scoped = ready.filter((v) => {
    if (scope === 'project') return !projectId || !v.projectId || v.projectId === projectId; // variants with no projectId are legacy/pre-this-task records — kept visible rather than silently hidden
    if (scope === 'creator') return !creatorId || !v.creatorId || v.creatorId === creatorId; // see getScheduledPosts() note on unlinked legacy records
    return true;
  });
  const groups = new Map();
  scoped.forEach((v) => {
    const key = v.assetId || v.id;
    if (!groups.has(key)) groups.set(key, { assetId: key, contentTitle: v.contentTitle || v.title || 'Untitled', contentKind: v.contentKind, creatorId: v.creatorId, projectId: v.projectId, variants: [] });
    groups.get(key).variants.push(v);
  });
  return Array.from(groups.values());
}

/**
 * Scheduled posts, scoped the same honest way the queue is. Legacy posts
 * (created directly by `Scheduler.jsx`, before this task) never carry a
 * `creatorId`/`projectId` — rather than hide them forever (which would
 * look like silently discarded data), they're treated as visible in
 * every scope. This is called out in the completion report, not hidden.
 */
export function getScheduledPosts({ projectId, creatorId, scope } = {}) {
  const all = getAllScheduledPosts();
  if (scope === 'project') return all.filter((p) => !p.projectId || p.projectId === projectId);
  if (scope === 'creator') return all.filter((p) => !p.creatorId || p.creatorId === creatorId);
  return all;
}

export function buildScheduledPost({ variant, platform, scheduledAt, timezone, batchId, templateId }) {
  return {
    // Legacy-compatible fields:
    id: newId('schedule'),
    variantId: variant.id,
    platform,
    scheduledAt, // ISO string
    title: variant.title,
    description: variant.description,
    hashtags: variant.hashtags,
    createdAt: new Date().toISOString(),
    // New, additive fields:
    assetId: variant.assetId,
    creatorId: variant.creatorId,
    projectId: variant.projectId,
    contentKind: variant.contentKind,
    contentTitle: variant.contentTitle,
    thumbnailUrl: variant.thumbnail?.url || variant.thumbnailLF || null,
    cta: variant.cta,
    status: 'scheduled', // scheduled | publishing | published | failed — see note in ScheduleWorkspace.jsx: nothing in this codebase ever auto-advances a FUTURE post past 'scheduled' (no backend worker exists — Level 3)
    timezone,
    batchId: batchId || null,
    templateId: templateId || null,
    updatedAt: new Date().toISOString(),
  };
}

/** Creates the post AND flips the source variant to 'scheduled' — the exact transition `Scheduler.jsx` already performs, so a variant can't be double-booked by accident. */
export function schedulePost({ variant, platform, scheduledAt, timezone, batchId, templateId }) {
  const post = buildScheduledPost({ variant, platform, scheduledAt, timezone, batchId, templateId });
  const posts = getAllScheduledPosts();
  saveAllScheduledPosts([...posts, post]);
  upsertVariant({ ...variant, status: 'scheduled' });
  return post;
}

export function updateScheduledPost(postId, patch) {
  const posts = getAllScheduledPosts();
  const next = posts.map((p) => (p.id === postId ? { ...p, ...patch, updatedAt: new Date().toISOString() } : p));
  saveAllScheduledPosts(next);
  return next.find((p) => p.id === postId);
}

/** Removing a scheduled post frees its variant back into the Content Queue (section 12). */
export function removeScheduledPost(postId) {
  const posts = getAllScheduledPosts();
  const post = posts.find((p) => p.id === postId);
  saveAllScheduledPosts(posts.filter((p) => p.id !== postId));
  if (post?.variantId) {
    const variant = getAllVariants().find((v) => v.id === post.variantId);
    if (variant && variant.status === 'scheduled') upsertVariant({ ...variant, status: 'ready' });
  }
}

/** Duplicating keeps the same content/variant reference but is its own independent post (section 12) — e.g. reposting evergreen content at a second time. Defaults to +1 day so it's never silently identical. */
export function duplicateScheduledPost(postId) {
  const posts = getAllScheduledPosts();
  const post = posts.find((p) => p.id === postId);
  if (!post) return null;
  const copy = { ...post, id: newId('schedule'), scheduledAt: new Date(new Date(post.scheduledAt).getTime() + 86400000).toISOString(), status: 'scheduled', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  saveAllScheduledPosts([...posts, copy]);
  return copy;
}

// ── Fill Next Available Slots (section 8) — deterministic, no AI. ──
const DAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

/** Every candidate datetime in `range` that lands on an allowed posting day at an allowed posting time. */
function candidateSlots({ startDate, endDate, postingDays, postingTimes }) {
  const slots = [];
  const allowedDays = new Set(postingDays.map((d) => DAY_INDEX[d]));
  const cursor = new Date(startDate);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  while (cursor <= end) {
    if (allowedDays.has(cursor.getDay())) {
      postingTimes.forEach((t) => {
        const [h, m] = t.split(':').map(Number);
        const slot = new Date(cursor);
        slot.setHours(h, m || 0, 0, 0);
        if (slot >= new Date()) slots.push(slot);
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return slots.sort((a, b) => a - b);
}

/**
 * Proposes placements for `items` (each `{variant, platform}`) into the
 * next free slots matching the rules, skipping any slot already occupied
 * by an existing post on the SAME platform (never double-books a
 * platform at the same minute unless the caller explicitly allows it).
 * Returns a preview array for the creator to review/edit before
 * anything is written to storage — nothing here persists by itself.
 */
export function fillNextAvailableSlots({ items, startDate, endDate, postingDays, postingTimes, frequencyPerDay = 1, existingPosts, allowSamePlatformSameVariant = false }) {
  const occupied = new Set(existingPosts.map((p) => `${p.platform}__${new Date(p.scheduledAt).toISOString()}`));
  const slotsByPlatform = {};
  const proposals = [];
  const skipped = [];

  items.forEach(({ variant, platform }) => {
    if (!allowSamePlatformSameVariant) {
      const alreadyScheduled = existingPosts.some((p) => p.variantId === variant.id && p.platform === platform);
      if (alreadyScheduled) { skipped.push({ variant, platform, reason: 'Already has a scheduled post on this platform.' }); return; }
    }
    if (!slotsByPlatform[platform]) slotsByPlatform[platform] = candidateSlots({ startDate, endDate, postingDays, postingTimes });
    const freeSlot = slotsByPlatform[platform].find((s) => !occupied.has(`${platform}__${s.toISOString()}`));
    if (!freeSlot) { skipped.push({ variant, platform, reason: 'No free slot left in this date range for this platform.' }); return; }
    occupied.add(`${platform}__${freeSlot.toISOString()}`);
    slotsByPlatform[platform] = slotsByPlatform[platform].filter((s) => s !== freeSlot);
    proposals.push({ variant, platform, proposedAt: freeSlot.toISOString() });
  });

  return { proposals, skipped };
}

// ── Time zone (section 13) — no server-side creator preference exists
// anywhere in this backend (checked all_models.py) — genuinely persisted
// per-browser only, defaulting to the browser's own IANA zone. ──
export function getCreatorTimezone(creatorId) {
  if (!creatorId) return Intl.DateTimeFormat().resolvedOptions().timeZone;
  return localStorage.getItem(TIMEZONE_KEY(creatorId)) || Intl.DateTimeFormat().resolvedOptions().timeZone;
}
export function setCreatorTimezone(creatorId, timezone) {
  if (!creatorId) return;
  localStorage.setItem(TIMEZONE_KEY(creatorId), timezone);
}

export const COMMON_TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
];

export const PUBLISHING_STATUSES = ['draft', 'ready', 'scheduled', 'publishing', 'published', 'failed'];

// ── Publish Now (section 6) — calls the REAL, already-existing backend
// route `/api/platforms/post-now`. That route is itself explicitly mock
// by default (`POSTING_MOCK_MODE=true`, see routers/platforms.py) — it
// genuinely enforces real approval gates and plan limits first, then
// returns `{status:"mocked", message:"[MOCK] Posting..."}` rather than
// actually calling YouTube/Instagram/etc. This wrapper never upgrades
// that into a bare "Published" without passing along the mock/blocked
// status it actually received — see section 15. ──
export async function publishNow({ apiUrl, connectionId, assetUrl, caption }) {
  const params = new URLSearchParams({ connection_id: connectionId, asset_url: assetUrl || '', caption: caption || '' });
  const res = await fetch(`${apiUrl}/api/platforms/post-now?${params.toString()}`, { method: 'POST', credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    const reason = (detail && typeof detail === 'object') ? (detail.reason || detail.detail) : detail;
    return { ok: false, blocked: true, reason: reason || data.reason || 'Publish request was blocked.', raw: data };
  }
  if (data.status === 'mocked') return { ok: true, mocked: true, message: data.message, raw: data };
  return { ok: true, mocked: false, raw: data };
}

export async function getPlatformConnections({ apiUrl, creatorId }) {
  try {
    const res = await fetch(`${apiUrl}/api/platforms/connections/${creatorId}`, { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json();
    return data.connections || data || [];
  } catch { return []; }
}

/**
 * Resolves the REAL publishable media file for a variant — never a
 * thumbnail. Only `contentKind:'video'` has a verified, single-step path
 * today: the same presigned `stream_url` the Editor/Optimize already use
 * (`GET /api/editor/projects/source/{assetId}`, source = the Parent/Child
 * ContentSource itself). Clips are stored as `assetId: EditorClip.id`
 * with no `editorProjectId` captured on the variant, so there is no
 * existing, verified API path from here to that clip's real rendered
 * file (`/api/editor/projects/{project_id}/clips/{clip_id}/download`
 * needs a project id this record doesn't have) — and designs are
 * local-only layouts with no server file at all. Both are reported as
 * unavailable rather than guessed at.
 */
export async function resolvePublishAssetUrl({ apiUrl, variant }) {
  if (variant.contentKind !== 'video') {
    return { ok: false, reason: `Publish Now isn't available yet for ${variant.contentKind === 'clip' ? 'Clips' : 'Images & Posts'} — the real published file can't be resolved from here.` };
  }
  try {
    const res = await fetch(`${apiUrl}/api/editor/projects/source/${variant.assetId}`, { credentials: 'include' });
    if (!res.ok) return { ok: false, reason: 'Could not load the source video for publishing.' };
    const data = await res.json();
    if (data.stream_url) return { ok: true, url: data.stream_url };
    return { ok: false, reason: data.stream_error || 'Source media is temporarily unavailable for publishing.' };
  } catch { return { ok: false, reason: 'Could not reach the server to resolve the source video.' }; }
}
